import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheck, Send } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { useTurnstile } from "@/components/common/turnstile";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api/endpoints";
import { applyApiError } from "@/lib/form-errors";
import { AuthLayout } from "./auth-layout";

const schema = z.object({
  email: z.string().min(1, "Enter your email").email("That doesn't look like an email"),
});

type Values = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const turnstile = useTurnstile("forgot-password");
  const [sent, setSent] = React.useState(false);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors("root");
    if (turnstile.required && !turnstile.token) {
      form.setError("root", { message: "Please complete the security check." });
      return;
    }
    try {
      await authApi.forgotPassword({
        email: values.email.trim(),
        turnstileToken: turnstile.token ?? "",
      });
      // Always 200 — the response never reveals whether the account exists.
      setSent(true);
    } catch (error) {
      turnstile.reset();
      applyApiError(error, form);
    }
  });

  if (sent) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle="If that email belongs to an account, a reset link is on its way."
      >
        <Alert variant="success">
          <MailCheck />
          <AlertDescription>
            The link expires shortly for safety. Resetting your password signs you out of every
            device.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline" className="mt-5 w-full">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to set a new one."
      footer={
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {form.formState.errors.root?.message && (
          <Alert variant="destructive">
            <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
          </Alert>
        )}
        <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" autoFocus {...form.register("email")} />
        </Field>
        {turnstile.widget}
        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <Send className="size-4" />
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
